/**
 * Bulk import carries no secrets by design: `toExport()` strips every credential
 * field before a data source leaves the API. A connector whose completeness rule
 * keys off a field that *does* survive that strip — Cloudinary's `environment`,
 * which is a cloud name rather than a secret — would otherwise reject its own
 * export, because the surviving field alone reads as a half-filled custom setup.
 *
 * Marking a configuration as credential-pending lets such a connector validate
 * its structure without its secrets, so the importable work (target, mapping,
 * params, context) lands and an administrator supplies the credentials
 * afterwards. Querying still fails loudly until they do.
 *
 * The mark is a non-enumerable symbol: it is invisible to spreads, `Object.keys`,
 * `JSON.stringify`, the config envelope's clone and TypeORM, so it cannot leak
 * into a stored configuration or an export. It lives only for the duration of a
 * single validation pass.
 */
const CREDENTIALS_PENDING = Symbol('dataSourceCredentialsPending');

export function markDataSourceCredentialsPending<T>(config: T): T {
	if (config === null || typeof config !== 'object') {
		return config;
	}

	Object.defineProperty(config, CREDENTIALS_PENDING, {
		value: true,
		enumerable: false,
		writable: false,
		configurable: true
	});

	return config;
}

export function isDataSourceCredentialsPending(config: unknown): boolean {
	return config !== null && typeof config === 'object' && (config as Record<symbol, unknown>)[CREDENTIALS_PENDING] === true;
}
